import { z } from 'zod';
import type {
  ApplyRecordWritesResult,
  GenerateRecordCodesResult,
  TaxonomyDataset,
  TaxonomyDatasetUser,
  TaxonomyRecord,
  TaxonomyRecordPage,
} from '../model/types';

const datasetUserSchema: z.ZodType<TaxonomyDatasetUser> = z.looseObject({
  id: z.string(),
  // Platform's user type says string, but nothing checks it; a blank or missing name reads as
  // null so the callers' fallbacks apply.
  name: z
    .string()
    .nullish()
    .transform((value) => value?.trim() || null),
  avatarUrl: z.string().nullable().optional(),
});

export const taxonomyDatasetSchema: z.ZodType<TaxonomyDataset> = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  taxonomyId: z.string(),
  taxonomyVersionId: z.string(),
  workspaceId: z.string().nullable(),
  campaignId: z.string().nullable(),
  recordCount: z.number(),
  createdBy: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  creator: datasetUserSchema.nullable().optional(),
  modifiedBy: datasetUserSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const taxonomyDatasetListSchema = z.array(taxonomyDatasetSchema);

const recordSchema: z.ZodType<TaxonomyRecord> = z.looseObject({
  id: z.string(),
  datasetId: z.string(),
  taxonomyVersionId: z.string(),
  cells: z.record(z.string(), z.union([z.string(), z.array(z.string()), z.null()])),
  assembledName: z.string().nullable(),
  outputs: z.record(z.string(), z.string()),
  sortKey: z.number(),
  revision: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const taxonomyRecordListSchema = z.array(recordSchema);

export const taxonomyRecordPageSchema: z.ZodType<TaxonomyRecordPage> = z.looseObject({
  data: z.array(recordSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

export const applyRecordWritesSchema: z.ZodType<ApplyRecordWritesResult> = z.looseObject({
  applied: z.array(
    z.looseObject({
      id: z.string(),
      revision: z.number(),
      assembledName: z.string().nullable(),
    }),
  ),
  conflicted: z.array(z.string()),
});

export const generateRecordCodesSchema: z.ZodType<GenerateRecordCodesResult> = z.looseObject({
  minted: z.array(z.looseObject({ id: z.string(), code: z.string() })),
  skipped: z.array(z.looseObject({ id: z.string(), reason: z.string() })),
  spent: z.number(),
});

export const deleteRecordsSchema = z.looseObject({ deletedCount: z.number() });
